import React, { useState, useEffect } from "react";
import { KFDropdown } from "@kf/ic2fx";
import MenuItem from "@mui/material/MenuItem";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../store/reducers";

import { PageView, ReportTypes } from "../../../constants/app.constant";
import Loader from "../../loader";
import PayGapBarChart from "../Charts/PayGapBarChart/PayGapBarChart";
import DistrubutionChart from "../Charts/DistrubutionChart/DistrubutionChart";
import { reportDataThunk } from "../../../store/slices/reportsSlice";
import styles from "./VariablePayReporting.module.scss";

const VariablePayReporting = ({ pageView = PageView.CHART }: any) => {
  const [peReportData, setPeReportData] = useState<any>({});
  const dispatch = useDispatch();

  const { globalFilters } = useSelector((state: RootState) => {
    return state.reports;
  });

  const { isReportsLoading, reportsData } = useSelector((state: RootState) => {
    return state.reports;
  });

  const initReportData = () => {
    const _globalFilters = { ...globalFilters };

    delete _globalFilters.hayPointsMin;
    delete _globalFilters.hayPointsMax;

    const payload = {
      type: ReportTypes.VARIABLE_PAY_REPORTING,
      filters: _globalFilters
    };

    dispatch(reportDataThunk(payload));
  };

  useEffect(() => {
    let { hourlyPayGap, bonusPayGap, genderDistribution } =
      reportsData[ReportTypes.VARIABLE_PAY_REPORTING] || {};

    if (hourlyPayGap) {
      hourlyPayGap = {
        female: +(+hourlyPayGap.female).toFixed(2),
        male: +(+hourlyPayGap.male).toFixed(2)
      };
      const total = hourlyPayGap.female + hourlyPayGap.male;
      hourlyPayGap = {
        ...hourlyPayGap,
        femaleAvg: +((hourlyPayGap.female / total) * 100).toFixed(2),
        maleAvg: +((hourlyPayGap.male / total) * 100).toFixed(2)
      };
    }

    if (bonusPayGap) {
      bonusPayGap = {
        female: +(+bonusPayGap.female).toFixed(2),
        male: +(+bonusPayGap.male).toFixed(2)
      };
      const total = bonusPayGap.female + bonusPayGap.male;
      bonusPayGap = {
        ...bonusPayGap,
        femaleAvg: +((bonusPayGap.female / total) * 100).toFixed(2),
        maleAvg: +((bonusPayGap.male / total) * 100).toFixed(2)
      };
    }

    const _reportsData = {
      ...reportsData,
      hourlyPayGap,
      bonusPayGap,
      genderDistribution: genderDistribution?.data || []
    };

    setPeReportData(_reportsData || {});
  }, [reportsData]);

  useEffect(() => {
    if (globalFilters) {
      initReportData();
    }
  }, [globalFilters]);

  return (
    <div className={styles.reportContainer}>
      {isReportsLoading && <Loader />}

      <div className={styles.description}>
        Please pick the variable you would like to use as dependent variable,
        independent variables and the DEI variables in the multivariate
        regression models.
      </div>
      <div className="reportFilter">
        <KFDropdown label="View by:" onChange={() => {}} value="">
          <MenuItem disableRipple value={""}>
            Median
          </MenuItem>
          <MenuItem disableRipple value={2}>
            Headcount
          </MenuItem>
          <MenuItem disableRipple value={3}>
            Headcount %
          </MenuItem>
        </KFDropdown>
      </div>
      {pageView === "LIST" && (
        <div className={styles.listContainer}>
          <div className={styles.listCards}>
            <div className={styles.listCard}>
              <div
                className={`${styles.listWrapper} ${styles.listWrapperWithRowSapn}`}
              >
                <div className={styles.leftRowSpan}>
                  Gender distribution by quartile
                </div>
                <div className={styles.listWrapper}>
                  <div className={styles.listItem}>
                    <div className={`${styles.listLabel} ${styles.listTitle}`}>
                      Quarter
                    </div>
                    <div className={`${styles.listLabel} ${styles.listTitle}`}>
                      Female
                    </div>
                    <div className={`${styles.listLabel} ${styles.listTitle}`}>
                      Male
                    </div>
                    <div className={`${styles.listLabel} ${styles.listTitle}`}>
                      Transgender
                    </div>
                    <div className={`${styles.listLabel} ${styles.listTitle}`}>
                      Nonbinary
                    </div>
                  </div>
                  {peReportData.genderDistribution &&
                    peReportData.genderDistribution.map((data: any) => {
                      return (
                        <div className={styles.listItem} key={data.quarters}>
                          <div className={styles.listLabel}>
                            {data.quarters}
                          </div>
                          <div className={styles.listLabel}>
                            {data.femalePercentage || 0}%
                          </div>
                          <div className={styles.listValue}>
                            {data.malePercentage || 0}%
                          </div>
                          <div className={styles.listValue}>
                            {data.transGenderPercentage || 0}%
                          </div>
                          <div className={styles.listValue}>
                            {data.nonbinaryPercentage || 0}%
                          </div>
                        </div>
                      );
                    })}
                </div>
              </div>
            </div>
            <div className={styles.listCard}>
              <div className={styles.listWrapper}>
                <div className={styles.listItem}>
                  <div
                    className={`${styles.listLabel} ${styles.listTitle}`}
                  ></div>
                  <div className={`${styles.listLabel} ${styles.listTitle}`}>
                    Female
                  </div>
                  <div className={`${styles.listLabel} ${styles.listTitle}`}>
                    Male
                  </div>
                  <div className={`${styles.listLabel} ${styles.listTitle}`}>
                    Gap
                  </div>
                </div>
                <div className={styles.listItem}>
                  <div className={styles.listLabel}>Male vs. Female</div>
                  <div className={styles.listLabel}>67.34</div>
                  <div className={styles.listLabel}>108.34</div>
                  <div className={styles.listValue}>35%</div>
                </div>
                <div className={styles.listItem}>
                  <div className={styles.listLabel}>White vs. Non-White</div>
                  <div className={styles.listLabel}>45</div>
                  <div className={styles.listLabel}>233</div>
                  <div className={styles.listValue}>35%</div>
                </div>
              </div>
            </div>
          </div>
          <div className={styles.chartInsight}>
            <div className={styles.insightTitle}>Insight</div>
            <div className={styles.chartDescription}>
              In this organization, women occupy 0% of the highest paid jobs and
              33% of the highest paid jobs. 45% of the organization’s employees
              are women.
            </div>
          </div>
        </div>
      )}
      {pageView === "CHART" && (
        <div className={styles.chartContainer}>
          <div className={styles.chartWrappers}>
            <div className={styles.chartWrapper}>
              <PayGapBarChart
                title="Hourly pay gap"
                description="In this organization, women earn 0.63 for every 1 that men earn
                when comparing median hourly pay. Their median hourly pay is 37%
                lower than men’s."
                data={[
                  peReportData.hourlyPayGap?.femaleAvg,
                  peReportData.hourlyPayGap?.maleAvg
                ]}
              />
            </div>
            <div className={styles.chartWrapper}>
              <PayGapBarChart
                title="Bonus pay gap"
                description="In this organization, women earn 0.77 for every 1 that men earn
                when comparing median bonus pay. Their median bonus pay is 23%
                lower than men’s."
                data={[
                  peReportData.bonusPayGap?.femaleAvg,
                  peReportData.bonusPayGap?.maleAvg
                ]}
              />
            </div>
          </div>
          <DistrubutionChart
            title="Gender distribution by quartile"
            description="This chart shows the average (mean) pay by gender and the
              percentage difference between them."
            data={peReportData.genderDistribution}
          />
        </div>
      )}
    </div>
  );
};

export default VariablePayReporting;
